/* eslint-disable max-classes-per-file */
import { LiveSttResult } from '@ftr/contracts/regional-api/live-stt'
import { Region } from '@ftr/contracts/type/court/Region'
import { Uuid } from '@ftr/contracts/type/shared'
import { RealTimePlaybackKey } from '@ftr/data-realtime-playback'
import { SttEditTransaction } from '@ftr/regional-api-contracts'
import { LocalDateTime, LocalTime } from '@js-joda/core'
import { PopoverEvent } from '../../lib/presentation/real-time-stt-popover/types'
import { RealTimeSttPauseConfiguration, RealTimeSttSegmentMap } from '../../types'
import { SttEditOperation } from './edit-operations'
import { SttEditOperationSideEffect } from './edit-operations/operation-side-effect'
import { RealTimeSttSourceStateModel } from './real-time-stt.model'

export interface RealTimeSttAction {
  readonly playbackKey: RealTimePlaybackKey
}

export class UpdateRealTimeSttSourceStateSegmentMapAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Update Source Segment Map'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly segmentMap: RealTimeSttSegmentMap,
  ) {}
}

interface SegmentMapSttConfiguration {
  pauseConfiguration: RealTimeSttPauseConfiguration
  canReadStt: boolean
  hideAfter: LocalDateTime | undefined
}
export class FetchRealTimeSttSegmentMapAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Fetch Source Segment Map'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly courtSystemId: Uuid,
    readonly recordingStartTime: LocalDateTime,
    readonly sttConfiguration: SegmentMapSttConfiguration,
  ) {}
}

export class UpsertRealTimeSttLiveRemarksAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Upsert Live Remarks'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly update: LiveSttResult,
  ) {}
}

export class StreamRealTimeSttSegmentMapLiveUpdatesAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Stream Segment Map Updates'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly courtSystemId: Uuid,
    readonly region: Region,
    readonly canAccessSealedContent: boolean,
  ) {}
}

export class SetRealTimeSttFailedToSetupConnectionAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Failed to setup RealTime stt connection'

  constructor(readonly playbackKey: RealTimePlaybackKey) {}
}
export class StopRealTimeSttSegmentMapLiveUpdatesAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Stop Streaming Segment Map Live Updates'

  constructor(readonly playbackKey: RealTimePlaybackKey) {}
}

export class SetRealTimeSttIsLivePinnedAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Set Stt is Live Pinned'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly isLivePinned: boolean,
  ) {}
}

export class SetRealTimeSttSourceStateAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Set Stt Source State'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly state: RealTimeSttSourceStateModel,
  ) {}
}

export class JumpRealTimeSttToTimestampAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Jump Stt To Timestamp'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly timestamp: LocalTime,
  ) {}
}

export class JumpRealTimeSttToTranscriptElementAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Jump Stt To Transcript Element'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly transcriptElementId: string,
    readonly segmentId: string,
  ) {}
}

export class JumpRealTimeSttToHearingSectionAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Jump Stt To Hearing Section'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly hearingSectionId: Uuid,
  ) {}
}

export type PlaybackMarkerHighlightType = 'firstInGroup' | 'remark'
export class HighlightRealTimeSttTranscriptElementsAtTimestamp implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Highlight remarks at timestamp'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly timestamp: LocalDateTime,
    readonly playbackMarkerHighlightType: PlaybackMarkerHighlightType,
  ) {}
}

export class SetHideAfterAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Set hide after'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly hideAfter: LocalDateTime,
  ) {}
}

export class FetchCourtroomSttModeAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Fetch Courtroom Stt Mode'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly courtSystemId: Uuid,
    readonly locationId: Uuid,
  ) {}
}

export class FetchRealTimeSttCanSealContentAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Fetch RealTime Stt Can Seal Content'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly courtSystemId: Uuid,
  ) {}
}

export class PerformSttEditOperationAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Perform Stt Edit Operation Action'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly courtSystemId: Uuid,
    readonly sttEditOperation: SttEditOperation,
  ) {}
}

export class ApplySttEditTransactionAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Apply Stt Edit Transaction'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly transaction: SttEditTransaction,
  ) {}
}

export class InvokeSttEditOperationSideEffectAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Invoke Stt Edit Operation Side Effects'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly sideEffect: SttEditOperationSideEffect,
  ) {}
}

export class QueuePersistSttEditTransactionAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Queue Persist Stt Edit Transaction'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly courtSystemId: Uuid,
    readonly transaction: SttEditTransaction,
  ) {}
}

export class TriggerSttEditPersistQueueProcessingAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Trigger Stt Edit Persist Queue Processing'
  constructor(readonly playbackKey: RealTimePlaybackKey) {}
}

export class PersistSttEditTransactionAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Persist Stt Edit Transaction'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly courtSystemId: Uuid,
    readonly transaction: SttEditTransaction,
  ) {}
}

export class SttPopoverAction implements RealTimeSttAction {
  static readonly type = '[RealTimeStt] Stt Popover Action'

  constructor(
    readonly playbackKey: RealTimePlaybackKey,
    readonly event: PopoverEvent,
  ) {}
}
